/**
 * The shape of a track, in the one form every consumer reads: PIECES of road.
 *
 * A piece is either a BAND — a centreline with a width at every node (the track, a branch, the pit
 * lane) — or an AREA (a closed outline filled with asphalt: the editor's "road area"). Both are
 * cubic Bezier chains: a node carries `hOut` (the handle leaving it) and `hIn` (the handle the
 * previous edge arrives at), and an edge with neither is a STRAIGHT LINE, i.e. one edge and not a run
 * of samples. Handles are absolute points, as the editor stores them.
 *
 * The editor used to flatten all of this before writing the file: 16 samples per curve, every
 * segment's endpoints stitched into one long `trackPath`, `gap` markers where the stitch didn't land
 * exactly, and the whole geometry written twice (the flattened path AND the segments it came from).
 * The readers then tried to recover the curves out of those samples with Douglas-Peucker plus
 * Catmull-Rom, and every defect of the last week came out of that round trip: wobbly straights,
 * curves drawn as a chain of little straight pieces, a white line breaking where two runs met. The
 * file now carries the curves and this module samples them, the same way for everybody, so the
 * asphalt that is painted IS the asphalt the physics tests — same points, by construction.
 *
 * Exported as a verbatim copy to the website (src/lib/trackEditor/trackGeometry.js, see the website's
 * `npm run sync:painter`), which draws the track picture with it. Edit HERE, then re-sync.
 */

export const GEOMETRY_STEP = 6; // world units between the samples of a curve
const MIN_STEPS = 4;            // a curve is never sampled in fewer pieces than this
const MAX_STEPS = 256;          // and never in more, however long it is

/** One node of a piece. `w` is the road's width there (bands only; falls back to the piece's own). */
export function nodeWidth(node, piece, roadWidth) {
  return node?.w ?? piece?.width ?? roadWidth ?? 180;
}

/** The width of the road at a point: its own `width` when it carries one (see roadRuns), the track's
 *  fallback otherwise. Every reader of a road's shape and every painter of it goes through this. */
export function widthOf(point, fallbackWidth) {
  return point.width ?? fallbackWidth ?? 180;
}

/**
 * The cubic that runs from node `i` to `i + 1`: the endpoints with their handles, a missing handle
 * being the third of the straight line it would be on — so the control points are always there,
 * every edge is a cubic, and an edge nobody curved IS the straight line.
 */
export function edgeControls(nodes, i, closed) {
  const a = nodes[i];
  const b = nodes[closed ? (i + 1) % nodes.length : i + 1];
  if (!a || !b) return null;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return {
    a,
    b,
    c1: a.hOut ?? { x: a.x + dx / 3, y: a.y + dy / 3 },
    c2: b.hIn ?? { x: a.x + (dx * 2) / 3, y: a.y + (dy * 2) / 3 },
    straight: !a.hOut && !b.hIn,
  };
}

/**
 * One edge as points. A straight edge stays its two endpoints: there is nothing in between, which is
 * what keeps a long straight from being a hundred collinear samples.
 *
 * `step` is the sampling distance along the control polygon (already the curve's length to within a
 * few percent, and much cheaper than measuring the real one), so the density follows the curve
 * instead of the number of nodes it happens to be drawn with.
 */
export function sampleEdge(edge, step = GEOMETRY_STEP) {
  if (!edge) return [];
  if (edge.straight) return [{ x: edge.a.x, y: edge.a.y }, { x: edge.b.x, y: edge.b.y }];
  const { a, b, c1, c2 } = edge;
  const hull = Math.hypot(c1.x - a.x, c1.y - a.y)
    + Math.hypot(c2.x - c1.x, c2.y - c1.y)
    + Math.hypot(b.x - c2.x, b.y - c2.y);
  const steps = Math.max(MIN_STEPS, Math.min(MAX_STEPS, Math.ceil(hull / step)));
  const points = [];
  for (let s = 0; s <= steps; s++) {
    const t = s / steps;
    const mt = 1 - t;
    points.push({
      x: mt * mt * mt * a.x + 3 * mt * mt * t * c1.x + 3 * mt * t * t * c2.x + t * t * t * b.x,
      y: mt * mt * mt * a.y + 3 * mt * mt * t * c1.y + 3 * mt * t * t * c2.y + t * t * t * b.y,
    });
  }
  return points;
}

/** The nodes of a piece, tolerating the two spellings the editor has used (`points` / `nodes`). */
export function pieceNodes(piece) {
  return piece?.nodes ?? piece?.points ?? [];
}

/**
 * A piece as a polyline. Bands come out with a `w` on every point — the width of the road there, so
 * the surfaces that are drawn and the ones that are driven can only ever be the same — and areas
 * come out as their closed ring.
 *
 * A closed piece does NOT repeat its first node at the end: the closing edge is generated here, so
 * there is no repeated vertex to special-case anywhere downstream (the ring's duplicated point used
 * to leave a chord straight across a road area's offset line, and a seam in a circuit's edge line).
 */
export function samplePiece(piece, { step = GEOMETRY_STEP, roadWidth } = {}) {
  const nodes = pieceNodes(piece);
  const band = piece?.kind !== 'area';
  // Closing is a property of the piece, and it has to be said: a circuit that loses its flag would
  // silently turn into an open road, whose last edge the painter would cap half a road away from the
  // first (see the site of the seam this used to leave in a circuit's edge line).
  const closed = piece?.closed === true && nodes.length > 2;
  if (nodes.length < 2) return { band, closed: false, points: [] };

  const points = [];
  const count = closed ? nodes.length : nodes.length - 1;
  for (let i = 0; i < count; i++) {
    const edge = edgeControls(nodes, i, closed);
    const sampled = sampleEdge(edge, step);
    if (!sampled.length) continue;
    if (band) {
      // The width travels with the samples, ramped along the edge: that is what makes a narrow
      // section (or a junction opening up) a property of the node, not of a separate geometry.
      const wFrom = nodeWidth(edge.a, piece, roadWidth);
      const wTo = nodeWidth(edge.b, piece, roadWidth);
      const last = Math.max(1, sampled.length - 1);
      for (let s = 0; s < sampled.length; s++) {
        // The first point of an edge is the previous edge's last one: kept once, from the edge it
        // belongs to, so a junction between two edges is a single vertex.
        if (i > 0 && s === 0) continue;
        const t = s / last;
        points.push({ ...sampled[s], w: wFrom + (wTo - wFrom) * t });
      }
    } else {
      for (let s = 0; s < sampled.length; s++) {
        if (i > 0 && s === 0) continue;
        points.push(sampled[s]);
      }
    }
  }
  return { band, closed, points };
}

/** True when the piece is a band (a centreline with a width) rather than a filled area. */
export function isBand(piece) {
  return piece?.kind !== 'area';
}
